import { html } from 'htm/preact'
import { useState, useEffect } from 'preact/hooks'
import { api } from '../api.js'
import { groupByCategory, money, displayPrice, displayUnit, displayMultiplier, defaultPerAmount, toStoredPrice } from '../units.js'
import CategorySection from '../components/CategorySection.js'
import Sheet from '../components/Sheet.js'
import UnitPicker from '../components/UnitPicker.js'

export default function Ingredients({ showToast }) {
  const [ingredients, setIngredients] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  // add/edit ingredient sheet
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editingIng, setEditingIng] = useState(null)
  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [newCatMode, setNewCatMode] = useState(false)
  const [price, setPrice] = useState('')
  const [perAmt, setPerAmt] = useState('100')
  const [unit, setUnit] = useState('g')

  // rename category sheet
  const [renameOpen, setRenameOpen] = useState(false)
  const [renamingCat, setRenamingCat] = useState('')
  const [renameInput, setRenameInput] = useState('')

  useEffect(() => { loadIngredients() }, [])

  async function loadIngredients() {
    setLoading(true)
    try {
      const d = await api('/api/ingredients')
      setIngredients(d.ingredients || [])
    } catch (e) {
      showToast(e.message, true)
    } finally {
      setLoading(false)
    }
  }

  // Derive categories from ingredient data — no separate API needed
  const allCats = [...new Set(ingredients.map(i => i.category).filter(Boolean))].sort()

  function filtered() {
    const q = search.trim().toLowerCase()
    if (!q) return ingredients
    return ingredients.filter(i =>
      i.name.toLowerCase().includes(q) || (i.category || '').toLowerCase().includes(q)
    )
  }

  // ── Ingredient CRUD ────────────────────────────────────────

  function openAdd(preselectedCat) {
    setEditingIng(null)
    setName('')
    setCategory(preselectedCat || '')
    setNewCatMode(false)
    setPrice('')
    setPerAmt('100')
    setUnit('g')
    setSheetOpen(true)
  }

  function openEdit(ing) {
    setEditingIng(ing)
    setName(ing.name)
    setCategory(ing.category || '')
    setNewCatMode(false)
    const mult = displayMultiplier(ing.unit)
    setPrice((ing.price_per_unit * mult).toFixed(2))
    setPerAmt(String(mult))
    setUnit(ing.unit)
    setSheetOpen(true)
  }

  async function handleSave() {
    if (!name.trim()) { showToast('Name required', true); return }
    const p = parseFloat(price)
    if (!Number.isFinite(p) || p < 0) { showToast('Valid price required', true); return }
    const storedPrice = toStoredPrice(p, parseFloat(perAmt))
    try {
      if (editingIng) {
        await api(`/api/ingredients/${editingIng.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: name.trim(), price_per_unit: storedPrice, unit, category: category.trim() || null }),
        })
        showToast('Saved')
      } else {
        await api('/api/ingredients', {
          method: 'POST',
          body: JSON.stringify({ name: name.trim(), price_per_unit: storedPrice, unit, category: category.trim() || null }),
        })
        showToast('Ingredient added')
      }
      setSheetOpen(false)
      await loadIngredients()
    } catch (e) {
      showToast(e.message, true)
    }
  }

  async function handleDelete(ing) {
    if (!confirm(`Remove "${ing.name}"?`)) return
    try {
      await api(`/api/ingredients/${ing.id}`, { method: 'DELETE' })
      showToast('Removed')
      await loadIngredients()
    } catch (e) {
      showToast(e.message, true)
    }
  }

  async function handlePriceSave(ing, newPrice) {
    if (!Number.isFinite(newPrice)) return
    const storedPrice = newPrice / displayMultiplier(ing.unit)
    try {
      await api(`/api/ingredients/${ing.id}`, {
        method: 'PUT',
        body: JSON.stringify({ price_per_unit: storedPrice }),
      })
      await loadIngredients()
    } catch (e) {
      showToast(e.message, true)
    }
  }

  // ── Category management (batch PUTs — no separate categories API) ──

  function openRename(catName) {
    setRenamingCat(catName)
    setRenameInput(catName)
    setRenameOpen(true)
  }

  async function handleRename() {
    const newName = renameInput.trim()
    if (!newName) { showToast('Name required', true); return }
    if (newName === renamingCat) { setRenameOpen(false); return }
    const affected = ingredients.filter(i => i.category === renamingCat)
    try {
      await Promise.all(affected.map(i =>
        api(`/api/ingredients/${i.id}`, {
          method: 'PUT',
          body: JSON.stringify({ category: newName }),
        })
      ))
      showToast('Category renamed')
      setRenameOpen(false)
      await loadIngredients()
    } catch (e) {
      showToast(e.message, true)
    }
  }

  async function handleDeleteCat(catName) {
    const n = ingredients.filter(i => i.category === catName).length
    const msg = n > 0
      ? `Move ${n} ingredient(s) from "${catName}" to Uncategorized?`
      : `Delete empty category "${catName}"?`
    if (!confirm(msg)) return
    const affected = ingredients.filter(i => i.category === catName)
    try {
      await Promise.all(affected.map(i =>
        api(`/api/ingredients/${i.id}`, {
          method: 'PUT',
          body: JSON.stringify({ category: null }),
        })
      ))
      showToast('Category removed')
      await loadIngredients()
    } catch (e) {
      showToast(e.message, true)
    }
  }

  if (loading) return html`<div class="empty">Loading…</div>`

  const filteredList = filtered()
  const groups = groupByCategory(filteredList)

  return html`
    <div class="screen-head">
      <h1>Ingredients</h1>
      <p class="sub">Prices update every recipe automatically</p>
    </div>
    <input type="search" class="search" placeholder="Search ingredients…" value=${search} onInput=${e => setSearch(e.target.value)} />

    ${groups.map(([cat, items]) => html`
      <${CategorySection}
        key=${cat || '__none__'}
        category=${cat}
        count=${items.length}
        total=${money(items.reduce((s, i) => s + displayPrice(i.price_per_unit, i.unit), 0))}
        onEdit=${cat ? () => openRename(cat) : null}
        onDelete=${cat ? () => handleDeleteCat(cat) : null}
      >
        <button type="button" class="add-inside-cat" onClick=${() => openAdd(cat)}>+ Add ingredient</button>
        ${items.map(i => {
          const dp = displayPrice(i.price_per_unit, i.unit)
          const du = displayUnit(i.unit)
          return html`
            <div class="ing-row" key=${i.id}>
              <span class="drag-handle" aria-hidden="true">≡</span>
              <span class="ing-name" onClick=${() => openEdit(i)}>${i.name}</span>
              <input
                type="number"
                class="price-input"
                value=${dp.toFixed(2)}
                min="0"
                step="0.01"
                aria-label="Price"
                onBlur=${e => handlePriceSave(i, parseFloat(e.target.value))}
              />
              <span class="ing-unit">/ ${du}</span>
              <button type="button" class="icon-btn" onClick=${() => handleDelete(i)} aria-label="Delete">✕</button>
            </div>
          `
        })}
      </${CategorySection}>
    `)}
    ${filteredList.length === 0 && html`
      <div class="empty">${search ? 'No ingredients match.' : 'No ingredients yet. Tap + to add one.'}</div>
    `}
    <button type="button" class="fab" aria-label="Add ingredient" onClick=${() => openAdd('')}>+</button>

    <${Sheet} open=${sheetOpen} onClose=${() => setSheetOpen(false)} title=${editingIng ? 'Edit Ingredient' : 'Add Ingredient'}>
      <div class="field">
        <label>Name</label>
        <input class="input" value=${name} onInput=${e => setName(e.target.value)} placeholder="e.g. Butter" autoComplete="off" />
      </div>
      <div class="field">
        <label>Category</label>
        ${newCatMode
          ? html`
            <div class="new-cat-inline">
              <input class="input" value=${category} onInput=${e => setCategory(e.target.value)} placeholder="New category name…" autoFocus />
              <button type="button" class="icon-btn" onClick=${() => { setNewCatMode(false); setCategory('') }} aria-label="Cancel">✕</button>
            </div>
          `
          : html`
            <select class="input" value=${category} onChange=${e => {
              if (e.target.value === '__new__') { setNewCatMode(true); setCategory('') }
              else setCategory(e.target.value)
            }}>
              <option value="">No category</option>
              ${allCats.map(c => html`<option value=${c}>${c}</option>`)}
              <option value="__new__">+ New category…</option>
            </select>
          `
        }
      </div>
      <div class="field">
        <label>Unit</label>
        <${UnitPicker} value=${unit} onChange=${u => { setUnit(u); setPerAmt(String(defaultPerAmount(u))) }} />
      </div>
      <div class="field">
        <label>Price (CAD)</label>
        <div class="price-per-row">
          <input class="input" type="number" min="0" step="0.01" value=${price} onInput=${e => setPrice(e.target.value)} placeholder="0.00" />
          <span class="price-per-sep">per</span>
          <input class="input price-per-amt" type="number" min="0.0001" step="any" value=${perAmt} onInput=${e => setPerAmt(e.target.value)} />
          <span class="price-per-unit">${unit}</span>
        </div>
        ${price && perAmt && html`<p class="price-per-hint">= ${money(displayPrice(toStoredPrice(parseFloat(price) || 0, parseFloat(perAmt) || 1), unit))} per ${displayUnit(unit)}</p>`}
      </div>
      <div class="sheet__actions">
        <button type="button" class="btn btn--ghost" onClick=${() => setSheetOpen(false)}>Cancel</button>
        <button type="button" class="btn btn--primary" onClick=${handleSave}>${editingIng ? 'Save' : 'Add'}</button>
      </div>
    </${Sheet}>

    <${Sheet} open=${renameOpen} onClose=${() => setRenameOpen(false)} title="Rename Category">
      <div class="field">
        <label>Name</label>
        <input class="input" value=${renameInput} onInput=${e => setRenameInput(e.target.value)} autoFocus />
      </div>
      <div class="sheet__actions">
        <button type="button" class="btn btn--ghost" onClick=${() => setRenameOpen(false)}>Cancel</button>
        <button type="button" class="btn btn--primary" onClick=${handleRename}>Save</button>
      </div>
    </${Sheet}>
  `
}
